/**
 * Cart Actions Emitter - Event emitter for cart actions
 *
 * Provides a simple pub/sub mechanism for cart actions
 * Used for cross-component communication and analytics tracking
 */

import type { Maybe } from '../types/front.graphql';

// Define the types of cart events
type CartEvents = {
    addItem: { productId: string; quantity: number };
    removeItem: { productId: string };
    clearCart: void;
    updateItem: { productId: string; quantity: number };
};

// Simple EventEmitter implementation for web
class SimpleEventEmitter<T extends Record<string, unknown>> {
    private listeners: Map<keyof T, Set<(data: T[keyof T]) => void>> = new Map();

    emit<K extends keyof T>(eventName: K, data: T[K]): void {
        const eventListeners = this.listeners.get(eventName);
        if (eventListeners) {
            eventListeners.forEach(callback => {
                try {
                    callback(data);
                } catch (error) {
                    console.error(`[EventEmitter] Error in listener for ${String(eventName)}:`, error);
                }
            });
        }
    }

    on<K extends keyof T>(eventName: K, callback: (data: T[K]) => void): void {
        if (!this.listeners.has(eventName)) {
            this.listeners.set(eventName, new Set());
        }
        this.listeners.get(eventName)!.add(callback as (data: T[keyof T]) => void);
    }

    off<K extends keyof T>(eventName: K, callback: (data: T[K]) => void): void {
        const eventListeners = this.listeners.get(eventName);
        if (eventListeners) {
            eventListeners.delete(callback as (data: T[keyof T]) => void);
        }
    }

    removeAllListeners<K extends keyof T>(eventName?: K): void {
        if (eventName) {
            this.listeners.delete(eventName);
        } else {
            this.listeners.clear();
        }
    }
}

// Create a singleton instance of EventEmitter for cart actions
class CartActionsEmitter {
    private readonly emitter = new SimpleEventEmitter<CartEvents>();

    // Add an item to the cart
    public addItem(productId: Maybe<number> | undefined, quantity: number): void {
        this.emitter.emit('addItem', { productId: `${productId ?? 'no-product-id'}`, quantity });
    }

    // Remove an item from the cart
    public removeItem(productId: string): void {
        this.emitter.emit('removeItem', { productId });
    }

    // Clear the cart
    public clearCart(): void {
        this.emitter.emit('clearCart', undefined as unknown as void);
    }

    // Update an item's quantity in the cart
    public updateItem(productId: string, quantity: number): void {
        this.emitter.emit('updateItem', { productId, quantity });
    }

    // Listen to a specific cart event
    public on<K extends keyof CartEvents>(eventName: K, callback: (data: CartEvents[K]) => void): void {
        this.emitter.on(eventName, callback);
    }

    // Stop listening to a specific cart event
    public off<K extends keyof CartEvents>(eventName: K, callback: (data: CartEvents[K]) => void): void {
        this.emitter.off(eventName, callback);
    }
}

// Export a singleton instance
export const cartActionsEmitter = new CartActionsEmitter();
